import React, { useEffect, useState } from "react";
import axios from "axios";
import { Buffer } from "buffer";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Page404 from "app/not-found";

const API_URL = process.env.REACT_APP_API_URL;
const BAR_COLORS = ["bg-blue-500", "bg-green-500", "bg-purple-500", "bg-yellow-500", "bg-pink-500"];

const PageSommaire = () => {
  const { user } = useSelector((state) => state.user);
  const navigate = useNavigate();
  const [projets, setProjets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [avatarPreviews, setAvatarPreviews] = useState({});
  const [votePreviews, setVotePreviews] = useState({}); // { projetId: [ { url, name } ] }
  const [animatedWidths, setAnimatedWidths] = useState({}); // pour animation

  useEffect(() => {
    const fetchProjets = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/projets`);
        const projetsData = res.data;

        // Récupérer votes pour chaque projet
        const votesData = await Promise.all(
          projetsData.map(async (projet) => {
            const votesRes = await axios.get(`${API_URL}/api/votes/${projet._id}`);
            return { projetId: projet._id, votes: votesRes.data };
          })
        );

        const totalVotes = votesData.reduce((sum, p) => sum + p.votes.length, 0);

        const projetsAvecPourcentage = projetsData.map((projet) => {
          const voteInfo = votesData.find((v) => v.projetId === projet._id);
          return {
            ...projet,
            votes: voteInfo ? voteInfo.votes : [],
            voteCount: voteInfo ? voteInfo.votes.length : 0,
            pourcentage: totalVotes ? ((voteInfo?.votes.length || 0) / totalVotes) * 100 : 0,
          };
        });

        projetsAvecPourcentage.sort((a, b) => b.pourcentage - a.pourcentage);
        setProjets(projetsAvecPourcentage);

        // Avatars auteurs
        const previews = {};
        await Promise.all(
          projetsAvecPourcentage.map(async (projet) => {
            if (projet.auteur?.img?.data) {
              const img = new Image();
              img.src = `data:${projet.auteur.img.contentType};base64,${Buffer.from(
                projet.auteur.img.data
              ).toString("base64")}`;

              await new Promise((resolve) => {
                img.onload = () => {
                  const displaySize = 32;
                  const scale = window.devicePixelRatio || 1;
                  const canvasSize = displaySize * scale;

                  const canvas = document.createElement("canvas");
                  canvas.width = canvasSize;
                  canvas.height = canvasSize;

                  const ctx = canvas.getContext("2d");
                  ctx.clearRect(0, 0, canvasSize, canvasSize);
                  ctx.imageSmoothingEnabled = true;
                  ctx.imageSmoothingQuality = "high";

                  const ratio = Math.max(canvasSize / img.width, canvasSize / img.height);
                  const newWidth = img.width * ratio;
                  const newHeight = img.height * ratio;
                  const dx = (canvasSize - newWidth) / 2;
                  const dy = (canvasSize - newHeight) / 2;

                  ctx.drawImage(img, dx, dy, newWidth, newHeight);
                  previews[projet._id] = canvas.toDataURL("image/png");
                  resolve(null);
                };
                img.onerror = () => resolve(null);
              });
            }
          })
        );
        setAvatarPreviews(previews);

        // Avatars votants
        const votePrevs = {};
        await Promise.all(
          projetsAvecPourcentage.map(async (projet) => {
            votePrevs[projet._id] = await Promise.all(
              projet.votes.map(async (vote) => {
                if (vote.user?.img?.data) {
                  const img = new Image();
                  img.src = `data:${vote.user.img.contentType};base64,${Buffer.from(
                    vote.user.img.data
                  ).toString("base64")}`;
                  const url = await new Promise((resolve) => {
                    img.onload = () => {
                      const displaySize = 24;
                      const scale = window.devicePixelRatio || 1;
                      const canvasSize = displaySize * scale;

                      const canvas = document.createElement("canvas");
                      canvas.width = canvasSize;
                      canvas.height = canvasSize;

                      const ctx = canvas.getContext("2d");
                      ctx.clearRect(0, 0, canvasSize, canvasSize);
                      ctx.imageSmoothingEnabled = true;
                      ctx.imageSmoothingQuality = "high";

                      const ratio = Math.max(canvasSize / img.width, canvasSize / img.height);
                      const newWidth = img.width * ratio;
                      const newHeight = img.height * ratio;
                      const dx = (canvasSize - newWidth) / 2;
                      const dy = (canvasSize - newHeight) / 2;

                      ctx.drawImage(img, dx, dy, newWidth, newHeight);
                      resolve(canvas.toDataURL("image/png"));
                    };
                    img.onerror = () => resolve(null);
                  });
                  return { url, name: vote.user.name || "Votant inconnu" };
                }
                return { url: null, name: vote.user?.name || "Votant inconnu" };
              })
            );
          })
        );
        setVotePreviews(votePrevs);

        // Animation barres
        const initialWidths = {};
        projetsAvecPourcentage.forEach((p) => (initialWidths[p._id] = 0));
        setAnimatedWidths(initialWidths);
        setTimeout(() => {
          const targetWidths = {};
          projetsAvecPourcentage.forEach((p) => (targetWidths[p._id] = p.pourcentage));
          setAnimatedWidths(targetWidths);
        }, 100);

      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchProjets();
  }, []);

  if (loading) return <p className="text-center mt-10">Chargement du sommaire…</p>;
  if (projets.length === 0) return <p className="text-center mt-10">Aucun projet disponible.</p>;

  if (!user) return <Page404 />;
  
  return (
    <div className="max-w-6xl mx-auto p-4">
      <h1 className="text-3xl font-bold text-center mb-8">Sommaire des votes</h1>

      <div className="flex flex-col gap-4">
        {projets.map((projet, index) => (
          <div
            key={projet._id}
            className="flex items-center gap-4"
          >
            {/* Avatar auteur */}
            <div className="relative group flex-shrink-0">
              {avatarPreviews[projet._id] ? (
                <img
                  src={avatarPreviews[projet._id]}
                  alt={projet.auteur?.name || "Auteur"}
                  className="w-8 h-8 rounded-full border border-gray-200"
                />
              ) : (
                <div className="w-8 h-8 rounded-full bg-gray-200" />
              )}
              <div className="absolute top-10 left-1/2 -translate-x-1/2
                              bg-black text-white text-xs px-2 py-1 rounded
                              opacity-0 group-hover:opacity-100 transition
                              whitespace-nowrap z-50">
                {projet.auteur?.name || "Auteur inconnu"}
              </div>
            </div>

            {/* Nom du projet */}
            <div
              className="w-40 font-semibold text-blue-600 cursor-pointer hover:underline flex-shrink-0"
              onClick={() => navigate(`/admin/projets/${projet._id}`)}
            >
              {projet.nom}
            </div>

            {/* Barre horizontale */}
            <div className="flex-1 h-6 bg-gray-200 rounded-full overflow-hidden relative">
              <div
                className={`${BAR_COLORS[index % BAR_COLORS.length]} h-full rounded-full transition-all duration-1000`}
                style={{ width: `${animatedWidths[projet._id] || 0}%` }}
              />
            </div>

            {/* Pourcentage + votes + avatars votants */}
            <div className="w-60 flex items-center gap-2">
            <span className="font-semibold text-left">
                {projet.pourcentage.toFixed(1)}% ({projet.voteCount})
            </span>
            <div className="flex -space-x-2">
                {votePreviews[projet._id]?.map(
                (vote, i) =>
                    vote.url && (
                    <div key={i} className="relative group">
                        <img
                        src={vote.url}
                        alt={vote.name}
                        className="w-6 h-6 rounded-full border border-white"
                        />
                        <div className="absolute top-6 left-1/2 -translate-x-1/2
                                        bg-black text-white text-xs px-2 py-1 rounded
                                        opacity-0 group-hover:opacity-100 transition
                                        whitespace-nowrap z-50">
                        {vote.name}
                        </div>
                    </div>
                    )
                )}
            </div>
            </div>

          </div>
        ))}
      </div>
    </div>
  );
};

export default PageSommaire;
